import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { toast } from 'sonner'
import { AlertCircle, Plus } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { useProductDetail } from '../hooks/use-product-detail'
import { useStockUsage, useStockUsageSave } from '../hooks/use-stock-usage'
import { StockUsagePartsEditor } from './stock-usage-parts-editor'
import { StockCorrectionDialog } from './stock-correction-dialog'
import { CreateVariantDialog } from './create-variant-dialog'
import type { StockUsageView } from '@/api/endpoints/product-stock'
import {
  availabilityQueryKey,
  productStockApi,
} from '@/api/endpoints/product-stock'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { ACTIONS } from '@/shared/constants/actions'
import { formatShare, formatStockQuantity } from '@/shared/lib/stock-quantity'
import { formatCurrency } from '@/shared/utils/format-currency'
import { cn } from '@/lib/utils'

interface StockUsageTabProps {
  sku: string
  /** Switches the product detail to its Info tab, where the base unit is set. */
  onOpenInfoTab?: () => void
}

const cardClass = 'rounded-lg border p-4 space-y-3'

/**
 * "Lagerverbrauch" (CHG-2026-09-stock-usage-and-price-twins, 10.2/10.3): for a stock article the
 * tracking and counting switches, its default usage, the base unit, "Verwendet in" and its
 * variants; for a dish its parts, its online availability per location and its variants.
 * A variant has no stock usage of its own; the product detail disables this tab for it.
 */
export function StockUsageTab({ sku, onOpenInfoTab }: StockUsageTabProps) {
  const { t: tProducts } = useTranslation('products')
  const { t: tCommon } = useTranslation('common')
  const { can } = usePermissions()
  const { data: product } = useProductDetail(sku)
  const { data: view, isLoading, error } = useStockUsage(sku)
  const saver = useStockUsageSave(sku)
  const [converting, setConverting] = useState(false)
  const [variantOpen, setVariantOpen] = useState(false)

  const isMerged = !!product?.mergedIntoSku
  const canEdit = can(ACTIONS.PRODUCT_EDIT) && !isMerged

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    )
  }

  if (error || !view) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>{tCommon('error')}</AlertTitle>
        <AlertDescription>{tProducts('stockUsage.loadError')}</AlertDescription>
      </Alert>
    )
  }

  if (view.isVariant) {
    return (
      <p className="text-sm text-muted-foreground">
        {tProducts('stockUsage.variantHasNone')}
      </p>
    )
  }

  const canCreateVariant =
    can(ACTIONS.PRODUCT_CREATE) && !isMerged && !product?.openPrice
  const showPartsEditor = view.isDish || converting
  const savedPartsKey = view.parts.map((p) => `${p.sku}:${p.qty}`).join('|')

  const saveParts = (parts: Array<{ sku: string; qty: number }>) =>
    saver.save({ parts }, (res) => {
      setConverting(false)
      toast.success(
        res.isDish
          ? tProducts('stockUsage.partsSaved')
          : tProducts('stockUsage.partsRemoved'),
      )
    })

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
      <div className="space-y-6">
        {showPartsEditor ? (
          <div className="space-y-3">
            <div className="space-y-1">
              <h2 className="text-lg font-semibold">
                {tProducts('stockUsage.partsTitle')}
              </h2>
              <p className="text-sm text-muted-foreground">
                {tProducts('stockUsage.partsDescription')}
              </p>
            </div>
            <StockUsagePartsEditor
              key={savedPartsKey}
              dishSku={view.sku}
              savedParts={view.parts}
              isDish={view.isDish}
              canEdit={canEdit}
              isPending={saver.isPending}
              onSave={saveParts}
              onCancel={view.isDish ? undefined : () => setConverting(false)}
            />
          </div>
        ) : (
          <ArticleSettings
            view={view}
            canEdit={canEdit}
            isPending={saver.isPending}
            onToggleTracked={(tracked) =>
              saver.save({ tracked }, () =>
                toast.success(
                  tracked
                    ? tProducts('stockUsage.trackingOnSaved')
                    : tProducts('stockUsage.trackingOffSaved'),
                ),
              )
            }
            onToggleFractional={(fractionalCounting) =>
              saver.save({ fractionalCounting }, () =>
                toast.success(tProducts('stockUsage.fractionalSaved')),
              )
            }
            onConvert={() => setConverting(true)}
            onOpenInfoTab={onOpenInfoTab}
          />
        )}
      </div>

      <div className="space-y-4">
        {view.isDish ? (
          can(ACTIONS.INVENTORY_VIEW) && (
            <DishOnlineAvailability sku={view.sku} />
          )
        ) : (
          <UsedInCard view={view} />
        )}
        <VariantsCard
          view={view}
          canCreate={canCreateVariant}
          onCreate={() => setVariantOpen(true)}
        />
      </div>

      <StockCorrectionDialog
        details={saver.pendingCorrection}
        productName={view.name}
        unit={view.stockUnit}
        isPending={saver.isPending}
        refreshed={saver.correctionRefreshed}
        onConfirm={saver.confirmCorrection}
        onCancel={saver.cancelCorrection}
      />

      {canCreateVariant && (
        <CreateVariantDialog
          open={variantOpen}
          onOpenChange={setVariantOpen}
          sourceSku={view.sku}
          sourceName={view.name}
          source={{
            isDish: view.isDish,
            unit: view.stockUnit,
            parts: view.parts,
          }}
        />
      )}
    </div>
  )
}

/**
 * "Teilmengen zählen" is required on while a dish or variant uses the article fractionally. The
 * server says so (`fractionalLocked`, which also sees variants of dishes); a backend without the
 * field falls back to what the view shows.
 */
function isFractionalLocked(view: StockUsageView): boolean {
  if (view.fractionalLocked !== undefined) return view.fractionalLocked
  return (
    view.usedIn.some((dish) => !Number.isInteger(dish.qty)) ||
    view.variants.some((variant) => !Number.isInteger(variant.share))
  )
}

function ArticleSettings({
  view,
  canEdit,
  isPending,
  onToggleTracked,
  onToggleFractional,
  onConvert,
  onOpenInfoTab,
}: {
  view: StockUsageView
  canEdit: boolean
  isPending: boolean
  onToggleTracked: (tracked: boolean) => void
  onToggleFractional: (fractional: boolean) => void
  onConvert: () => void
  onOpenInfoTab?: () => void
}) {
  const { t: tProducts } = useTranslation('products')
  const fractionalLocked = view.fractionalCounting && isFractionalLocked(view)
  const isPart = view.usedIn.some((entry) => !entry.variantOf)

  return (
    <>
      <div className={cardClass}>
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <h2 className="text-base font-semibold">
              {tProducts('stockUsage.trackedTitle')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {tProducts('stockUsage.trackedDescription')}
            </p>
          </div>
          <Switch
            checked={view.stockTracked}
            onCheckedChange={onToggleTracked}
            disabled={!canEdit || isPending}
            aria-label={tProducts('stockUsage.trackedTitle')}
          />
        </div>
        <Separator />
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <h3 className="text-sm font-semibold">
              {tProducts('stockUsage.fractionalTitle')}
            </h3>
            <p className="text-sm text-muted-foreground">
              {fractionalLocked
                ? tProducts('stockUsage.fractionalLocked')
                : tProducts('stockUsage.fractionalDescription')}
            </p>
          </div>
          <Switch
            checked={view.fractionalCounting}
            onCheckedChange={onToggleFractional}
            disabled={!canEdit || isPending || fractionalLocked}
            aria-label={tProducts('stockUsage.fractionalTitle')}
          />
        </div>
        <Separator />
        <div className="space-y-2">
          <p className="text-xs font-semibold text-muted-foreground">
            {tProducts('stockUsage.defaultUsageTitle')}
          </p>
          <div className="flex items-center justify-between rounded-md border px-3 py-2">
            <span className="text-sm">
              {tProducts('stockUsage.defaultUsage', { unit: view.stockUnit })}
            </span>
            <Badge variant="secondary">
              {tProducts('stockUsage.defaultBadge')}
            </Badge>
          </div>
          {canEdit && (
            <div className="space-y-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onConvert}
                disabled={isPending || isPart}
              >
                {tProducts('stockUsage.convertAction')}
              </Button>
              {isPart && (
                <p className="text-xs text-muted-foreground">
                  {tProducts('stockUsage.convertBlockedIsPart')}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      <div className={cardClass}>
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-base font-semibold">
            {tProducts('packageUnits.baseUnit')}
          </h2>
          {onOpenInfoTab && (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0"
              onClick={onOpenInfoTab}
            >
              {tProducts('stockUsage.baseUnitChangeLink')}
            </Button>
          )}
        </div>
        <p className="text-sm font-medium">{view.stockUnit}</p>
        <p className="text-xs text-muted-foreground">
          {tProducts('stockUsage.baseUnitHint')}
        </p>
      </div>
    </>
  )
}

function UsedInCard({ view }: { view: StockUsageView }) {
  const { t: tProducts } = useTranslation('products')
  const unit = view.stockUnit
  return (
    <div className={cardClass}>
      <h2 className="text-base font-semibold">
        {tProducts('stockUsage.usedInTitle')}
      </h2>
      <ul className="space-y-2">
        {/* Dishes, each followed by its variants (qty = part × share, from the server). */}
        {view.usedIn.map((entry) => (
          <li key={entry.sku} className={entry.variantOf ? 'pl-4' : undefined}>
            <Link
              to={`/products/$sku` as '/products/$sku'}
              params={{ sku: entry.sku }}
              className="flex justify-between gap-3 rounded-md border px-3 py-2 text-sm hover:bg-accent"
            >
              <span className="truncate">
                {entry.name}
                {entry.variantOf && (
                  <span className="text-muted-foreground">
                    {' '}
                    {tProducts('stockUsage.usedInVariant')}
                  </span>
                )}
              </span>
              <span className="shrink-0 text-muted-foreground">
                {formatStockQuantity(entry.qty)} {unit}
              </span>
            </Link>
          </li>
        ))}
        {view.variants.map((variant) => (
          <li
            key={variant.sku}
            className="flex justify-between gap-3 px-3 py-1 text-sm"
          >
            <span className="truncate">
              {variant.name}{' '}
              <span className="text-muted-foreground">
                {tProducts('stockUsage.usedInVariant')}
              </span>
            </span>
            <span className="shrink-0 text-muted-foreground">
              {formatStockQuantity(variant.share)} {unit}
            </span>
          </li>
        ))}
        <li className="flex justify-between gap-3 px-3 py-1 text-sm">
          <span className="truncate">
            {tProducts('stockUsage.usedInDirect', { name: view.name })}
          </span>
          <span className="shrink-0 text-muted-foreground">1 {unit}</span>
        </li>
      </ul>
    </div>
  )
}

function VariantsCard({
  view,
  canCreate,
  onCreate,
}: {
  view: StockUsageView
  canCreate: boolean
  onCreate: () => void
}) {
  const { t: tProducts } = useTranslation('products')
  const { t: tCommon } = useTranslation('common')
  const { can } = usePermissions()
  const canViewPrice = can(ACTIONS.PRICE_VIEW)
  return (
    <div className={cardClass}>
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold">
          {tProducts('variant.listTitle')}
        </h2>
        {canCreate && (
          <Button type="button" variant="outline" size="sm" onClick={onCreate}>
            <Plus className="mr-1 h-4 w-4" />
            {tProducts('variant.createAction')}
          </Button>
        )}
      </div>
      {view.variants.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {tProducts('variant.listEmpty')}
        </p>
      ) : (
        <ul className="space-y-2">
          {view.variants.map((variant) => (
            <li key={variant.sku}>
              <Link
                to={`/products/$sku` as '/products/$sku'}
                params={{ sku: variant.sku }}
                className="flex flex-col gap-1 rounded-md border px-3 py-2 text-sm hover:bg-accent"
              >
                <span className="flex items-center justify-between gap-3">
                  <span className="truncate">{variant.name}</span>
                  <span className="shrink-0 text-muted-foreground">
                    {tProducts('variant.shareShort', {
                      share: formatShare(variant.share),
                    })}
                  </span>
                </span>
                {canViewPrice && (variant.prices?.length ?? 0) > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {variant.prices
                      ?.map(
                        (price) =>
                          `${price.restaurantName ?? price.restaurantId} ${formatCurrency(price.amountCents)}`,
                      )
                      .join(' · ')}
                  </span>
                )}
                {!variant.active && (
                  <Badge variant="secondary" className="whitespace-normal">
                    {variant.pausedBySource
                      ? tProducts('variant.paused')
                      : tCommon('inactive')}
                  </Badge>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
      <p className="text-xs text-muted-foreground">
        {view.isDish
          ? tProducts('variant.listHintDish')
          : tProducts('variant.listHintArticle')}
      </p>
    </div>
  )
}

/**
 * The dish's availability per location (D8), from one `GET …/availability`: the online number
 * the guest menu shows and the in-store number the POS tile shows, with the part that runs out
 * first online. Needs `inventory:view`.
 */
function DishOnlineAvailability({ sku }: { sku: string }) {
  const { t: tProducts } = useTranslation('products')
  const { data, isLoading, error } = useQuery({
    queryKey: availabilityQueryKey(sku),
    queryFn: () => productStockApi.availability(sku),
  })
  const items = data?.items ?? []

  return (
    <div className={cardClass}>
      <h2 className="text-base font-semibold">
        {tProducts('stockUsage.onlineTitle')}
      </h2>
      {isLoading ? (
        <Skeleton className="h-10 w-full" />
      ) : error ? (
        <p className="text-sm text-destructive">
          {tProducts('stockUsage.onlineLoadError')}
        </p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {tProducts('stockUsage.onlineNoRestaurants')}
        </p>
      ) : (
        <ul className="space-y-3">
          {items.map((item) => {
            const soldOut = !item.unlimited && item.onlineQty === 0
            return (
              <li key={item.restaurantId} className="space-y-0.5">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-sm">
                    {item.restaurantName ?? String(item.restaurantId)}
                  </span>
                  <span
                    className={cn(
                      'text-xl font-semibold tabular-nums',
                      soldOut && 'text-destructive',
                    )}
                  >
                    {item.unlimited
                      ? tProducts('stockUsage.onlineUnlimited')
                      : (item.onlineQty ?? 0)}
                  </span>
                </div>
                {!item.unlimited && (
                  <p className="text-xs text-muted-foreground">
                    {tProducts('stockUsage.inStore', {
                      qty:
                        item.inStoreQty === null
                          ? '–'
                          : formatStockQuantity(item.inStoreQty),
                    })}
                    {item.limitingName &&
                      ` · ${
                        soldOut
                          ? tProducts('stockUsage.onlineSoldOutBy', {
                              name: item.limitingName,
                            })
                          : tProducts('stockUsage.onlineLimitedBy', {
                              name: item.limitingName,
                            })
                      }`}
                  </p>
                )}
              </li>
            )
          })}
        </ul>
      )}
      <p className="text-xs text-muted-foreground">
        {tProducts('stockUsage.onlineHint')}
      </p>
    </div>
  )
}
